import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import FormInput from '../components/auth/FormInput';

const changePasswordSchema = z.object({
  currentPassword: z.string().min(8, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  confirmNewPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmNewPassword, {
  message: "Passwords don't match",
  path: ['confirmNewPassword'],
});

export default function ChangePassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');
    setIsSuccess(false);
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        setError('No authenticated user found.');
        console.error('[ChangePassword] No authenticated user found.');
        setIsLoading(false);
        return;
      }
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, data.currentPassword);
      console.log('[ChangePassword] Reauthenticating user:', user.email);
      await reauthenticateWithCredential(user, credential);
      // Update password
      console.log('[ChangePassword] Updating password for user:', user.email);
      await updatePassword(user, data.newPassword);
      setIsSuccess(true);
      reset();
      console.log('[ChangePassword] Password changed successfully.');
    } catch (err) {
      let message = 'An error occurred. Please try again.';
      if (err.code === 'auth/wrong-password') {
        message = 'Current password is incorrect.';
      } else if (err.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/weak-password') {
        message = 'Password is too weak.';
      } else if (err.code === 'auth/requires-recent-login') {
        message = 'Please log in again and try changing your password.';
      } else if (err.message) {
        message += ` [${err.message}]`;
      }
      setError(message);
      console.error('[ChangePassword] Error changing password:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Change Password</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormInput
          label="Current password"
          id="currentPassword"
          type="password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
        <FormInput
          label="New password"
          id="newPassword"
          type="password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <FormInput
          label="Confirm new password"
          id="confirmNewPassword"
          type="password"
          autoComplete="new-password"
          error={errors.confirmNewPassword?.message}
          {...register('confirmNewPassword')}
        />
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}
        {isSuccess && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Password changed successfully.</h3>
              </div>
            </div>
          </div>
        )}
        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orrekie-600 hover:bg-orrekie-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orrekie-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Changing password...' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
} 